import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Collapse,
  Form,
  Modal,
  OverlayTrigger,
  Spinner,
  Table,
  Tooltip,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faEdit, faPlus, faSyncAlt, faTrash } from '@fortawesome/free-solid-svg-icons';
import AddPayForm from '../Forms/AddPayForm';
import { handleError } from '../../utils/errorHandling';
import {
  configurePaySeriesAutoMaintain,
  commitExtendPaySeries,
  deleteScheduledPays,
  getAllPaysForSettings,
  previewDeleteScheduledPays,
  previewExtendPaySeries,
  scopedUpdatePay,
  fetchPaysInSeries,
} from '../../services/PayService';

function localIsoDateFromPay(d) {
  if (!d) return '';
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return '';
  const yyyy = x.getFullYear();
  const mm = String(x.getMonth() + 1).padStart(2, '0');
  const dd = String(x.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function todayIsoLocal() {
  const n = new Date();
  const yyyy = n.getFullYear();
  const mm = String(n.getMonth() + 1).padStart(2, '0');
  const dd = String(n.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function formatApiError(err, ctx) {
  const data = err.response?.data;
  if (!data) return handleError(err, ctx);
  if (typeof data === 'string') return data;
  if (data.detail) {
    return typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
  }
  const keys = Object.keys(data);
  if (!keys.length) return handleError(err, ctx);
  const val = data[keys[0]];
  return Array.isArray(val) ? val[0] : String(val);
}

function buildSeriesRows(pays) {
  const today = todayIsoLocal();
  const bySeries = new Map();
  for (const pay of pays) {
    const sid = String(pay.pay_series_id);
    if (!bySeries.has(sid)) bySeries.set(sid, []);
    bySeries.get(sid).push(pay);
  }

  return Array.from(bySeries.entries()).map(([sid, rows]) => {
    const sorted = [...rows].sort((a, b) => new Date(a.next_pay) - new Date(b.next_pay));
    const upcoming = sorted.filter((p) => localIsoDateFromPay(p.next_pay) >= today);
    const past = sorted.filter((p) => localIsoDateFromPay(p.next_pay) < today);
    const representative = upcoming[0] || sorted[sorted.length - 1];
    return {
      pay_series_id: sid,
      representative,
      nickname: representative?.nickname || '',
      amount: representative?.amount || '',
      frequency: representative?.frequency || '',
      nextPay: upcoming[0]?.next_pay || null,
      lastPay: sorted[sorted.length - 1]?.next_pay || null,
      upcomingCount: upcoming.length,
      pastCount: past.length,
      allPays: sorted,
      hasFuture: upcoming.length > 0,
      autoMaintainMinUpcoming: Number(representative?.auto_maintain_min_upcoming || 0),
    };
  }).sort((a, b) => {
    const aDate = a.nextPay || a.lastPay;
    const bDate = b.nextPay || b.lastPay;
    return new Date(aDate) - new Date(bDate);
  });
}

const EXPANDED_PAY_BATCH_SIZE = 10;
const MAX_AUTO_MAINTAIN_UPCOMING = 104;

const ActionButton = ({ icon, label, variant, onClick, disabled = false }) => (
  <OverlayTrigger placement="top" overlay={<Tooltip>{label}</Tooltip>}>
    <span className="me-1 d-inline-block">
      <Button
        variant={variant}
        size="sm"
        disabled={disabled}
        aria-label={label}
        style={{ width: '34px', display: 'inline-flex', justifyContent: 'center', alignItems: 'center' }}
        onClick={(e) => {
          e.stopPropagation();
          onClick(e);
        }}
      >
        <FontAwesomeIcon icon={icon} />
      </Button>
    </span>
  </OverlayTrigger>
);

const ManagePaysSection = ({ onNotify }) => {
  const [allPays, setAllPays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [expandedSeriesId, setExpandedSeriesId] = useState(null);
  const [expandedLoadingId, setExpandedLoadingId] = useState(null);
  const [expandedErrors, setExpandedErrors] = useState({});
  const [expandedPaysBySeries, setExpandedPaysBySeries] = useState({});
  const [showPastBySeries, setShowPastBySeries] = useState({});
  const [visibleUpcomingBySeries, setVisibleUpcomingBySeries] = useState({});

  const [extendOpen, setExtendOpen] = useState(false);
  const [extendTarget, setExtendTarget] = useState(null);
  const [extendMode, setExtendMode] = useState('count');
  const [extendCount, setExtendCount] = useState('1');
  const [extendUntil, setExtendUntil] = useState('');
  const [extendPreview, setExtendPreview] = useState(null);
  const [extendPreviewError, setExtendPreviewError] = useState('');
  const [extendPreviewLoading, setExtendPreviewLoading] = useState(false);
  const [extendBusy, setExtendBusy] = useState(false);
  const extendSubmitRef = useRef(false);

  const [autoMaintainOpen, setAutoMaintainOpen] = useState(false);
  const [autoMaintainTarget, setAutoMaintainTarget] = useState(null);
  const [autoMaintainMin, setAutoMaintainMin] = useState('0');
  const [autoMaintainBusy, setAutoMaintainBusy] = useState(false);
  const [autoMaintainError, setAutoMaintainError] = useState('');
  const autoMaintainSubmitRef = useRef(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editContext, setEditContext] = useState('series');
  const [editPay, setEditPay] = useState(null);
  const [editNickname, setEditNickname] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [editScope, setEditScope] = useState('this_and_future');
  const [editHasFuture, setEditHasFuture] = useState(true);
  const [editPeriodStart, setEditPeriodStart] = useState(todayIsoLocal());
  const [editPeriodUntil, setEditPeriodUntil] = useState('');
  const [editBusy, setEditBusy] = useState(false);
  const editSubmitRef = useRef(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePay, setDeletePay] = useState(null);
  const [deleteScope, setDeleteScope] = useState('this_and_future');
  const [deletePeriodStart, setDeletePeriodStart] = useState(todayIsoLocal());
  const [deletePeriodUntil, setDeletePeriodUntil] = useState('');
  const [deleteConfirmSeries, setDeleteConfirmSeries] = useState(false);
  const [deleteClearPaid, setDeleteClearPaid] = useState(false);
  const [deletePreview, setDeletePreview] = useState(null);
  const [deletePreviewLoading, setDeletePreviewLoading] = useState(false);
  const [deletePreviewError, setDeletePreviewError] = useState('');
  const [deleteNeedsConfirm, setDeleteNeedsConfirm] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const deleteSubmitRef = useRef(false);

  const seriesRows = useMemo(() => buildSeriesRows(allPays), [allPays]);

  const loadPays = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const rows = await getAllPaysForSettings();
      const safeRows = Array.isArray(rows) ? rows : [];
      const toMaintain = buildSeriesRows(safeRows).filter((row) => (
        row.autoMaintainMinUpcoming > 0 && row.upcomingCount < row.autoMaintainMinUpcoming
      ));
      if (toMaintain.length > 0) {
        await Promise.all(toMaintain.map((row) => (
          configurePaySeriesAutoMaintain(row.pay_series_id, row.autoMaintainMinUpcoming)
        )));
        const refreshedRows = await getAllPaysForSettings();
        setAllPays(Array.isArray(refreshedRows) ? refreshedRows : []);
      } else {
        setAllPays(safeRows);
      }
    } catch (e) {
      setError(formatApiError(e, 'ManagePaysSection'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPays();
  }, [loadPays]);

  const periodImpactCount = useMemo(() => {
    if (!editPay || editScope !== 'pay_period' || !editPeriodStart || !editPeriodUntil) return null;
    return allPays.filter((p) => (
      String(p.pay_series_id) === String(editPay.pay_series_id) &&
      localIsoDateFromPay(p.next_pay) >= editPeriodStart &&
      localIsoDateFromPay(p.next_pay) <= editPeriodUntil
    )).length;
  }, [allPays, editPay, editPeriodStart, editPeriodUntil, editScope]);

  const deleteImpactCount = useMemo(() => {
    if (!deletePay) return null;
    const today = todayIsoLocal();
    return allPays.filter((p) => {
      if (String(p.pay_series_id) !== String(deletePay.pay_series_id)) return false;
      const payDate = localIsoDateFromPay(p.next_pay);
      if (payDate < today) return false;
      if (deleteScope === 'this_pay') return p.id === deletePay.id;
      if (deleteScope === 'this_and_future') return payDate >= localIsoDateFromPay(deletePay.next_pay);
      if (deleteScope === 'all_pays') return true;
      if (deleteScope === 'pay_period') {
        return deletePeriodStart && deletePeriodUntil && payDate >= deletePeriodStart && payDate <= deletePeriodUntil;
      }
      return false;
    }).length;
  }, [allPays, deletePay, deletePeriodStart, deletePeriodUntil, deleteScope]);

  const toggleSeriesPays = async (row) => {
    const sid = row.pay_series_id;
    if (expandedSeriesId === sid) {
      setExpandedSeriesId(null);
      return;
    }
    setExpandedSeriesId(sid);
    setShowPastBySeries((prev) => ({ ...prev, [sid]: false }));
    setVisibleUpcomingBySeries((prev) => ({ ...prev, [sid]: prev[sid] || EXPANDED_PAY_BATCH_SIZE }));
    if (expandedPaysBySeries[sid]) return;
    setExpandedLoadingId(sid);
    setExpandedErrors((prev) => ({ ...prev, [sid]: '' }));
    try {
      const rows = await fetchPaysInSeries(row.representative.id);
      setExpandedPaysBySeries((prev) => ({ ...prev, [sid]: Array.isArray(rows) ? rows : [] }));
    } catch (e) {
      setExpandedErrors((prev) => ({ ...prev, [sid]: formatApiError(e, 'ManagePaysSection-list') }));
    } finally {
      setExpandedLoadingId(null);
    }
  };

  const openExtend = (pay) => {
    setExtendTarget(pay);
    setExtendMode('count');
    setExtendCount('1');
    setExtendUntil('');
    setExtendPreview(null);
    setExtendPreviewError('');
    setExtendOpen(true);
  };

  const buildExtendBody = useCallback(() => {
    if (!extendTarget) return null;
    if (extendMode === 'count') {
      const count = parseInt(extendCount, 10);
      if (!Number.isFinite(count) || count < 1) return null;
      return { mode: 'count', additional_count: count };
    }
    if (!extendUntil) return null;
    return { mode: 'until_date', until_date: extendUntil };
  }, [extendCount, extendMode, extendTarget, extendUntil]);

  const runExtendPreview = async () => {
    const body = buildExtendBody();
    if (!extendTarget || !body) {
      setExtendPreview(null);
      return;
    }
    setExtendPreviewLoading(true);
    setExtendPreviewError('');
    try {
      const sid = extendTarget.pay_series_id;
      const preview = await previewExtendPaySeries(sid, body);
      setExtendPreview(preview);
    } catch (e) {
      setExtendPreviewError(formatApiError(e, 'ManagePaysSection'));
      setExtendPreview(null);
    } finally {
      setExtendPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (!extendOpen || !extendTarget) return undefined;
    setExtendPreview(null);
    setExtendPreviewError('');
    const timer = setTimeout(() => {
      runExtendPreview();
    }, 350);
    return () => clearTimeout(timer);
  }, [extendOpen, extendTarget, extendMode, extendCount, extendUntil]); // eslint-disable-line react-hooks/exhaustive-deps

  const commitExtend = async () => {
    if (extendSubmitRef.current) return;
    extendSubmitRef.current = true;
    setExtendBusy(true);
    setError('');
    try {
      const sid = extendTarget.pay_series_id;
      const body = buildExtendBody();
      if (!body) {
        setExtendPreviewError('Enter valid extension details.');
        return;
      }
      if (extendPreview?.base_last_next_pay) {
        body.base_last_next_pay = extendPreview.base_last_next_pay;
      }
      await commitExtendPaySeries(sid, body);
      setExtendOpen(false);
      await loadPays();
      if (onNotify) onNotify('Pay schedule extended.');
    } catch (e) {
      setError(formatApiError(e, 'ManagePaysSection'));
    } finally {
      extendSubmitRef.current = false;
      setExtendBusy(false);
    }
  };

  const openAutoMaintain = (row) => {
    setAutoMaintainTarget(row);
    setAutoMaintainMin(String(row.autoMaintainMinUpcoming || 0));
    setAutoMaintainError('');
    setAutoMaintainOpen(true);
  };

  const submitAutoMaintain = async () => {
    if (autoMaintainSubmitRef.current) return;
    autoMaintainSubmitRef.current = true;
    setAutoMaintainBusy(true);
    setAutoMaintainError('');
    try {
      const min = parseInt(autoMaintainMin, 10);
      if (!Number.isFinite(min) || min < 0 || min > MAX_AUTO_MAINTAIN_UPCOMING) {
        setAutoMaintainError(`Enter a number from 0 to ${MAX_AUTO_MAINTAIN_UPCOMING}.`);
        return;
      }
      const result = await configurePaySeriesAutoMaintain(autoMaintainTarget.pay_series_id, min);
      setAutoMaintainOpen(false);
      await loadPays();
      if (onNotify) {
        const topUpText = result.created_count
          ? ` ${result.created_count} new scheduled pay${result.created_count === 1 ? '' : 's'} created.`
          : '';
        onNotify(`Auto-maintain ${min > 0 ? `set to ${min} upcoming pays` : 'turned off'}.${topUpText}`);
      }
    } catch (e) {
      setAutoMaintainError(formatApiError(e, 'ManagePaysSection-auto-maintain'));
    } finally {
      autoMaintainSubmitRef.current = false;
      setAutoMaintainBusy(false);
    }
  };

  const openSeriesEdit = (row) => {
    setEditContext('series');
    setEditPay(row.representative);
    setEditNickname(row.nickname || '');
    setEditAmount(String(row.amount ?? ''));
    setEditScope(row.hasFuture ? 'this_and_future' : 'all_pays');
    setEditHasFuture(row.hasFuture);
    setEditPeriodStart(todayIsoLocal());
    setEditPeriodUntil('');
    setEditOpen(true);
  };

  const openPayEdit = (pay) => {
    const isPast = localIsoDateFromPay(pay.next_pay) < todayIsoLocal();
    setEditContext(isPast ? 'past_pay' : 'pay');
    setEditPay(pay);
    setEditNickname(pay.nickname || '');
    setEditAmount(String(pay.amount ?? ''));
    setEditScope('this_pay');
    setEditHasFuture(true);
    setEditPeriodStart(todayIsoLocal());
    setEditPeriodUntil('');
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (editSubmitRef.current) return;
    editSubmitRef.current = true;
    setEditBusy(true);
    setError('');
    try {
      const body = {
        pay_id: editPay.id,
        scope: editScope,
        nickname: editNickname,
      };
      if (editContext !== 'past_pay' && editScope !== 'all_pays') {
        body.amount = editAmount;
      }
      if (editScope === 'pay_period') {
        body.period_start_date = editPeriodStart;
        body.period_until_date = editPeriodUntil;
      }
      await scopedUpdatePay(body);
      setEditOpen(false);
      await loadPays();
      if (expandedSeriesId && editPay) {
        const rows = await fetchPaysInSeries(editPay.id);
        setExpandedPaysBySeries((prev) => ({ ...prev, [expandedSeriesId]: Array.isArray(rows) ? rows : [] }));
      }
      if (onNotify) onNotify('Pay updated.');
    } catch (e) {
      setError(formatApiError(e, 'ManagePaysSection'));
    } finally {
      editSubmitRef.current = false;
      setEditBusy(false);
    }
  };

  const openDelete = (pay, scope = 'this_pay') => {
    setDeletePay(pay);
    setDeleteScope(scope);
    setDeletePeriodStart(todayIsoLocal());
    setDeletePeriodUntil('');
    setDeleteConfirmSeries(false);
    setDeleteClearPaid(false);
    setDeletePreview(null);
    setDeletePreviewError('');
    setDeleteNeedsConfirm(false);
    setDeleteOpen(true);
  };

  const buildDeleteBody = useCallback(() => {
    if (!deletePay) return null;
    const body = {
      pay_id: deletePay.id,
      scope: deleteScope,
      confirm_remove_entire_series: deleteConfirmSeries,
      clear_paid_before_delete: deleteClearPaid,
    };
    if (deleteScope === 'pay_period') {
      if (!deletePeriodUntil) return null;
      body.period_start_date = deletePeriodStart;
      body.period_until_date = deletePeriodUntil;
    }
    return body;
  }, [deleteClearPaid, deleteConfirmSeries, deletePay, deletePeriodStart, deletePeriodUntil, deleteScope]);

  useEffect(() => {
    if (!deleteOpen || !deletePay) return undefined;
    setDeletePreview(null);
    setDeletePreviewError('');
    setDeleteNeedsConfirm(false);
    const body = buildDeleteBody();
    if (!body) return undefined;

    const timer = setTimeout(async () => {
      setDeletePreviewLoading(true);
      try {
        const preview = await previewDeleteScheduledPays(deletePay.pay_series_id, body);
        setDeletePreview(preview);
        setDeleteNeedsConfirm(!!preview.requires_confirm_remove_entire_series);
      } catch (e) {
        setDeletePreviewError(formatApiError(e, 'ManagePaysSection-delete-preview'));
      } finally {
        setDeletePreviewLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [buildDeleteBody, deleteOpen, deletePay]);

  const submitDelete = async () => {
    if (deleteSubmitRef.current) return;
    deleteSubmitRef.current = true;
    setDeleteBusy(true);
    setError('');
    try {
      const sid = deletePay.pay_series_id;
      const refreshAnchor = (expandedPaysBySeries[expandedSeriesId] || []).find((p) => p.id !== deletePay.id);
      const body = buildDeleteBody();
      if (!body) {
        setDeletePreviewError('Enter valid delete details.');
        return;
      }
      const result = await deleteScheduledPays(sid, body);
      setDeleteOpen(false);
      await loadPays();
      if (expandedSeriesId) {
        if (refreshAnchor) {
          const rows = await fetchPaysInSeries(refreshAnchor.id);
          setExpandedPaysBySeries((prev) => ({ ...prev, [expandedSeriesId]: Array.isArray(rows) ? rows : [] }));
        } else {
          setExpandedPaysBySeries((prev) => ({ ...prev, [expandedSeriesId]: [] }));
        }
      }
      if (onNotify) {
        onNotify(`${result.deleted_count || 0} scheduled pay${result.deleted_count === 1 ? '' : 's'} removed.`);
      }
    } catch (e) {
      const msg = formatApiError(e, 'ManagePaysSection');
      setError(msg);
      if (e.response?.data?.confirm_remove_entire_series) {
        setDeleteNeedsConfirm(true);
      }
    } finally {
      deleteSubmitRef.current = false;
      setDeleteBusy(false);
    }
  };

  return (
    <div className="manage-pays-section">
      <h3 className="page-title">Manage pays</h3>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Spinner animation="border" role="status" className="m-3">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      ) : (
        <>
          <h5 className="mt-3">Pay series</h5>
          <Table striped bordered hover responsive size="sm">
            <thead>
              <tr>
                <th>Nickname</th>
                <th>Amount</th>
                <th>Next pay</th>
                <th>Frequency</th>
                <th>Scheduled</th>
                <th>Auto-maintain</th>
                <th>Actions</th>
                <th aria-label="Expand"></th>
              </tr>
            </thead>
            <tbody>
              {seriesRows.map((row) => {
                const expanded = expandedSeriesId === row.pay_series_id;
                const seriesPays = expandedPaysBySeries[row.pay_series_id] || [];
                const upcomingPays = seriesPays.filter((p) => localIsoDateFromPay(p.next_pay) >= todayIsoLocal());
                const pastPays = seriesPays.filter((p) => localIsoDateFromPay(p.next_pay) < todayIsoLocal());
                const showPast = !!showPastBySeries[row.pay_series_id];
                const visibleUpcomingCount = visibleUpcomingBySeries[row.pay_series_id] || EXPANDED_PAY_BATCH_SIZE;
                const visibleUpcomingPays = upcomingPays.slice(0, visibleUpcomingCount);
                const hiddenUpcomingCount = Math.max(0, upcomingPays.length - visibleUpcomingPays.length);
                return (
                  <React.Fragment key={row.pay_series_id}>
                    <tr
                      onClick={() => toggleSeriesPays(row)}
                      style={{ cursor: 'pointer' }}
                      aria-expanded={expanded}
                    >
                      <td>{row.nickname}</td>
                      <td>{row.amount}</td>
                      <td>{row.nextPay ? localIsoDateFromPay(row.nextPay) : <span className="text-muted">None</span>}</td>
                      <td>{row.frequency}</td>
                      <td>
                        <Badge bg={row.upcomingCount ? 'success' : 'secondary'}>{row.upcomingCount} upcoming</Badge>
                      </td>
                      <td>
                        {row.autoMaintainMinUpcoming > 0 ? (
                          <Badge bg="info">Keep {row.autoMaintainMinUpcoming}</Badge>
                        ) : (
                          <span className="text-muted">Off</span>
                        )}
                      </td>
                      <td>
                        <ButtonGroup size="sm">
                          <ActionButton icon={faPlus} label="Extend series" variant="outline-success" onClick={() => openExtend(row.representative)} />
                          <ActionButton icon={faSyncAlt} label="Auto-maintain" variant="outline-info" onClick={() => openAutoMaintain(row)} />
                          <ActionButton icon={faEdit} label="Edit series" variant="outline-primary" onClick={() => openSeriesEdit(row)} />
                          <ActionButton
                            icon={faTrash}
                            label={row.hasFuture ? 'Delete scheduled pays' : 'No scheduled pays to delete'}
                            variant="outline-danger"
                            disabled={!row.hasFuture}
                            onClick={() => openDelete(row.representative, 'this_and_future')}
                          />
                        </ButtonGroup>
                      </td>
                      <td className="text-center">
                        <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} />
                      </td>
                    </tr>
                    {expanded && (
                      <tr>
                        <td colSpan="8" className="bg-light">
                          {expandedLoadingId === row.pay_series_id && <Spinner animation="border" size="sm" className="me-2" />}
                          {expandedErrors[row.pay_series_id] && <Alert variant="danger">{expandedErrors[row.pay_series_id]}</Alert>}
                          {!expandedErrors[row.pay_series_id] && expandedLoadingId !== row.pay_series_id && (
                            <div className="p-2">
                              {upcomingPays.length > 0 ? (
                                <>
                                  <Table striped bordered hover responsive size="sm" className="mb-2">
                                    <thead>
                                      <tr>
                                        <th>Pay date</th>
                                        <th>Amount</th>
                                        <th>Frequency</th>
                                        <th>Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {visibleUpcomingPays.map((p) => (
                                        <tr key={p.id}>
                                          <td>{localIsoDateFromPay(p.next_pay)}</td>
                                          <td>{p.amount}</td>
                                          <td>{p.frequency}</td>
                                          <td>
                                            <ButtonGroup size="sm">
                                              <ActionButton icon={faEdit} label="Edit pay" variant="outline-primary" onClick={() => openPayEdit(p)} />
                                              <ActionButton icon={faTrash} label="Delete pay" variant="outline-danger" onClick={() => openDelete(p, 'this_pay')} />
                                            </ButtonGroup>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                  {hiddenUpcomingCount > 0 && (
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                      <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        onClick={() => setVisibleUpcomingBySeries((prev) => ({
                                          ...prev,
                                          [row.pay_series_id]: visibleUpcomingCount + EXPANDED_PAY_BATCH_SIZE,
                                        }))}
                                      >
                                        Show {Math.min(EXPANDED_PAY_BATCH_SIZE, hiddenUpcomingCount)} more
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="link"
                                        onClick={() => setVisibleUpcomingBySeries((prev) => ({
                                          ...prev,
                                          [row.pay_series_id]: upcomingPays.length,
                                        }))}
                                      >
                                        Show all
                                      </Button>
                                      <small className="text-muted">
                                        Showing {visibleUpcomingPays.length} of {upcomingPays.length} upcoming pays
                                      </small>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <p className="text-muted mb-2">No upcoming pays in this series.</p>
                              )}
                              {pastPays.length > 0 && (
                                <>
                                  <Button
                                    variant="link"
                                    className="ps-0"
                                    onClick={() => setShowPastBySeries((prev) => ({ ...prev, [row.pay_series_id]: !showPast }))}
                                    aria-expanded={showPast}
                                  >
                                    {showPast ? 'Hide past pays' : `Show past pays (${pastPays.length})`}
                                  </Button>
                                  <Collapse in={showPast}>
                                    <div>
                                      <Table striped bordered hover responsive size="sm">
                                        <thead>
                                          <tr>
                                            <th>Pay date</th>
                                            <th>Amount</th>
                                            <th>Frequency</th>
                                            <th>Actions</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {pastPays.map((p) => (
                                            <tr key={p.id}>
                                              <td>{localIsoDateFromPay(p.next_pay)}</td>
                                              <td>{p.amount}</td>
                                              <td>{p.frequency}</td>
                                              <td>
                                                <ActionButton icon={faEdit} label="Edit pay name" variant="outline-primary" onClick={() => openPayEdit(p)} />
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </Table>
                                    </div>
                                  </Collapse>
                                </>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>

          <hr />
          <h4>Add a new pay</h4>
          <AddPayForm onSuccess={() => { loadPays(); if (onNotify) onNotify('Pay added successfully!'); }} />
        </>
      )}

      <Modal show={extendOpen} onHide={() => !extendBusy && setExtendOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Extend pay series</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {extendTarget && (
            <>
              <p>
                New pays are added after the <strong>last scheduled date</strong> in this pay schedule.
              </p>
              <Form.Group className="mb-2">
                <Form.Label>How to extend</Form.Label>
                <div>
                  <Form.Check
                    type="radio"
                    label="Add a number of pays"
                    name="extendMode"
                    checked={extendMode === 'count'}
                    onChange={() => {
                      setExtendMode('count');
                    }}
                  />
                  <Form.Check
                    type="radio"
                    label="Extend until a date (inclusive)"
                    name="extendMode"
                    checked={extendMode === 'until_date'}
                    onChange={() => {
                      setExtendMode('until_date');
                    }}
                  />
                </div>
              </Form.Group>
              {extendMode === 'count' ? (
                <Form.Group className="mb-2">
                  <Form.Label>Additional pays</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    value={extendCount}
                    onChange={(e) => setExtendCount(e.target.value)}
                  />
                </Form.Group>
              ) : (
                <Form.Group className="mb-2">
                  <Form.Label>Until date</Form.Label>
                  <Form.Control
                    type="date"
                    value={extendUntil}
                    onChange={(e) => setExtendUntil(e.target.value)}
                  />
                </Form.Group>
              )}
              {extendPreviewLoading && <Alert variant="secondary" className="mt-3">Calculating preview...</Alert>}
              {extendPreviewError && <Alert variant="danger" className="mt-3">{extendPreviewError}</Alert>}
              {extendPreview && (
                <Alert variant="info" className="mt-3">
                  New pays: <strong>{extendPreview.created_count}</strong>. Last new pay date:{' '}
                  <strong>{extendPreview.last_next_pay}</strong>.
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setExtendOpen(false)} disabled={extendBusy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={commitExtend} disabled={extendBusy || extendPreviewLoading || !extendPreview}>
            {extendBusy ? 'Working...' : 'Confirm extend'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={autoMaintainOpen} onHide={() => !autoMaintainBusy && setAutoMaintainOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Auto-maintain pay series</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {autoMaintainTarget && (
            <>
              <p>
                Keep a minimum number of <strong>upcoming</strong> pays materialised for{' '}
                <strong>{autoMaintainTarget.nickname}</strong>. When fewer upcoming pays remain, Budgit creates
                more scheduled pays from the last pay in the series.
              </p>
              <Form.Group className="mb-2">
                <Form.Label>Minimum upcoming pays</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  max={MAX_AUTO_MAINTAIN_UPCOMING}
                  value={autoMaintainMin}
                  onChange={(e) => setAutoMaintainMin(e.target.value)}
                />
                <Form.Text className="text-muted">
                  Use 0 to turn this off. The top-up still respects the two-year scheduling limit.
                </Form.Text>
              </Form.Group>
              {autoMaintainTarget.autoMaintainMinUpcoming > 0 && (
                <Alert variant="secondary" className="mt-3">
                  Currently keeping at least <strong>{autoMaintainTarget.autoMaintainMinUpcoming}</strong>{' '}
                  upcoming pay{autoMaintainTarget.autoMaintainMinUpcoming === 1 ? '' : 's'}.
                </Alert>
              )}
              {autoMaintainError && <Alert variant="danger" className="mt-3">{autoMaintainError}</Alert>}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setAutoMaintainOpen(false)} disabled={autoMaintainBusy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submitAutoMaintain} disabled={autoMaintainBusy}>
            {autoMaintainBusy ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={editOpen} onHide={() => !editBusy && setEditOpen(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editContext === 'series' ? 'Edit pay series' : 'Edit pay'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editContext === 'series' && (
            <Form.Group className="mb-2">
              <Form.Label>Scope</Form.Label>
              <Form.Select value={editScope} onChange={(e) => setEditScope(e.target.value)}>
                <option value="this_and_future" disabled={!editHasFuture}>This and future pays</option>
                <option value="all_pays">All pays in series</option>
                <option value="pay_period" disabled={!editHasFuture}>Pays in a period</option>
              </Form.Select>
            </Form.Group>
          )}
          {editScope === 'pay_period' && (
            <div className="d-flex gap-2">
              <Form.Group className="mb-2 flex-fill">
                <Form.Label>From date</Form.Label>
                <Form.Control type="date" value={editPeriodStart} onChange={(e) => setEditPeriodStart(e.target.value)} />
              </Form.Group>
              <Form.Group className="mb-2 flex-fill">
                <Form.Label>To date</Form.Label>
                <Form.Control type="date" value={editPeriodUntil} onChange={(e) => setEditPeriodUntil(e.target.value)} />
              </Form.Group>
            </div>
          )}
          {editScope === 'pay_period' && editPeriodUntil && (
            <Alert variant="info">
              This will update <strong>{periodImpactCount ?? 0}</strong> pay{periodImpactCount === 1 ? '' : 's'}.
            </Alert>
          )}
          <Form.Group className="mb-2">
            <Form.Label>Nickname</Form.Label>
            <Form.Control value={editNickname} onChange={(e) => setEditNickname(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Amount</Form.Label>
            <Form.Control
              value={editAmount}
              disabled={editScope === 'all_pays' || editContext === 'past_pay'}
              onChange={(e) => setEditAmount(e.target.value)}
            />
            {(editScope === 'all_pays' || editContext === 'past_pay') && (
              <Form.Text className="text-muted">Only the name can be changed for this scope.</Form.Text>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditOpen(false)} disabled={editBusy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submitEdit} disabled={editBusy || (editScope === 'pay_period' && !editPeriodUntil)}>
            {editBusy ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={deleteOpen} onHide={() => !deleteBusy && setDeleteOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete scheduled pays</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label>Scope</Form.Label>
            <Form.Select
              value={deleteScope}
              onChange={(e) => {
                setDeleteScope(e.target.value);
                setDeleteConfirmSeries(false);
                setDeleteClearPaid(false);
                setDeletePreview(null);
                setDeletePreviewError('');
                setDeleteNeedsConfirm(false);
              }}
            >
              <option value="this_pay">This pay only</option>
              <option value="this_and_future">This and future pays</option>
              <option value="all_pays">All scheduled pays in series</option>
              <option value="pay_period">Pays in a period</option>
            </Form.Select>
          </Form.Group>
          {deleteScope === 'pay_period' && (
            <div className="d-flex gap-2">
              <Form.Group className="mb-2 flex-fill">
                <Form.Label>From date</Form.Label>
                <Form.Control type="date" value={deletePeriodStart} onChange={(e) => setDeletePeriodStart(e.target.value)} />
              </Form.Group>
              <Form.Group className="mb-2 flex-fill">
                <Form.Label>To date</Form.Label>
                <Form.Control type="date" value={deletePeriodUntil} onChange={(e) => setDeletePeriodUntil(e.target.value)} />
              </Form.Group>
            </div>
          )}
          {deletePreviewLoading && <Alert variant="secondary">Checking scheduled pays...</Alert>}
          {deletePreviewError && <Alert variant="danger">{deletePreviewError}</Alert>}
          {(deletePreview || deleteImpactCount !== null) && (
            <Alert variant="info">
              This will delete{' '}
              <strong>{deletePreview?.delete_count ?? deleteImpactCount}</strong>{' '}
              scheduled pay{(deletePreview?.delete_count ?? deleteImpactCount) === 1 ? '' : 's'}.
            </Alert>
          )}
          {deletePreview?.paid_count > 0 && (
            <Alert variant="warning">
              <strong>{deletePreview.paid_count}</strong> scheduled pay{deletePreview.paid_count === 1 ? ' is' : 's are'} marked paid.
              Because this screen now treats transfers as the source of truth, you can explicitly mark
              {deletePreview.paid_count === 1 ? ' it' : ' them'} unpaid before deleting.
            </Alert>
          )}
          {deletePreview?.paid_count > 0 && (
            <Form.Check
              type="checkbox"
              className="mb-2"
              label="Mark these scheduled pays unpaid before deleting."
              checked={deleteClearPaid}
              onChange={(e) => setDeleteClearPaid(e.target.checked)}
            />
          )}
          {deleteNeedsConfirm && (
            <Alert variant="warning">
              This action removes every scheduled future pay in this schedule. Past pays will be kept. Tick the box
              below, then choose Delete again.
            </Alert>
          )}
          {deleteNeedsConfirm && (
            <Form.Check
              type="checkbox"
              label="I understand this removes every scheduled future pay for this series."
              checked={deleteConfirmSeries}
              onChange={(e) => setDeleteConfirmSeries(e.target.checked)}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteOpen(false)} disabled={deleteBusy}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={submitDelete}
            disabled={
              deleteBusy ||
              deletePreviewLoading ||
              (deleteScope === 'pay_period' && !deletePeriodUntil) ||
              (deletePreview?.paid_count > 0 && !deleteClearPaid) ||
              (deleteNeedsConfirm && !deleteConfirmSeries)
            }
          >
            {deleteBusy ? 'Working...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default ManagePaysSection;
